import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import ImagePositionEditor from './ImagePositionEditor';
import YouTubeVideoInput from './YouTubeVideoInput';
import './FeaturedProgramsAdmin.css';

const ExpertsTeamAdmin = ({ contentPath }) => {
  const { getContent, updateContent, content } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);

  useEffect(() => {
    setSectionData(getContent(contentPath));
  }, [contentPath, getContent, content]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
  };

  const handleTrainerUpdate = (index, field, value) => {
    updateContent(`${contentPath}.trainers.${index}.${field}`, value);
  };

  const handleMoveTrainer = (index, direction) => {
    const trainers = [...(sectionData?.trainers || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= trainers.length) return;
    [trainers[index], trainers[targetIndex]] = [trainers[targetIndex], trainers[index]];
    updateContent(`${contentPath}.trainers`, trainers);
  };

  const handleCvUpdate = (trainerIndex, field, value) => {
    updateContent(`${contentPath}.trainers.${trainerIndex}.cv.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleCvArrayUpdate = (trainerIndex, field, itemIndex, value) => {
    updateContent(`${contentPath}.trainers.${trainerIndex}.cv.${field}.${itemIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddCvArrayItem = (trainerIndex, field) => {
    const trainer = sectionData.trainers[trainerIndex];
    const items = trainer.cv?.[field] || [];
    updateContent(`${contentPath}.trainers.${trainerIndex}.cv.${field}`, [...items, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteCvArrayItem = (trainerIndex, field, itemIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const items = (trainer.cv?.[field] || []).filter((_, i) => i !== itemIndex);
    updateContent(`${contentPath}.trainers.${trainerIndex}.cv.${field}`, items);
    setSectionData(getContent(contentPath));
  };

  const handleSpecialtyUpdate = (trainerIndex, specialtyIndex, value) => {
    updateContent(`${contentPath}.trainers.${trainerIndex}.specialties.${specialtyIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddSpecialty = (trainerIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const specialties = trainer.specialties || [];
    updateContent(`${contentPath}.trainers.${trainerIndex}.specialties`, [...specialties, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteSpecialty = (trainerIndex, specialtyIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const specialties = trainer.specialties.filter((_, i) => i !== specialtyIndex);
    updateContent(`${contentPath}.trainers.${trainerIndex}.specialties`, specialties);
    setSectionData(getContent(contentPath));
  };

  const handleFormationUpdate = (trainerIndex, formationIndex, value) => {
    updateContent(`${contentPath}.trainers.${trainerIndex}.formations.${formationIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddFormation = (trainerIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const formations = trainer.formations || [];
    updateContent(`${contentPath}.trainers.${trainerIndex}.formations`, [...formations, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteFormation = (trainerIndex, formationIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const formations = trainer.formations.filter((_, i) => i !== formationIndex);
    updateContent(`${contentPath}.trainers.${trainerIndex}.formations`, formations);
    setSectionData(getContent(contentPath));
  };

  const handleAddPresentationVideo = (trainerIndex, videoData) => {
    const trainer = sectionData.trainers[trainerIndex];
    const videos = trainer.presentationVideos || [];
    const presentationVideo = {
      youtube_url: videoData.youtube_url,
      youtube_id: videoData.youtube_id,
      thumbnail: videoData.thumbnail,
      title: videoData.title || ''
    };
    updateContent(`${contentPath}.trainers.${trainerIndex}.presentationVideos`, [...videos, presentationVideo]);
    setSectionData(getContent(contentPath));
  };

  const handleDeletePresentationVideo = (trainerIndex, videoIndex) => {
    const trainer = sectionData.trainers[trainerIndex];
    const videos = (trainer.presentationVideos || []).filter((_, i) => i !== videoIndex);
    updateContent(`${contentPath}.trainers.${trainerIndex}.presentationVideos`, videos);
    setSectionData(getContent(contentPath));
  };

  const handleAddTrainer = () => {
    const trainers = sectionData?.trainers || [];
    const newTrainer = {
      id: '',
      name: '',
      title: '',
      photo: '',
      specialties: [],
      cv: {
        experience: '',
        certifications: [],
        achievements: []
      },
      formations: [],
      presentationVideos: [],
      country: '',
      countryFlag: ''
    };

    updateContent(`${contentPath}.trainers`, [...trainers, newTrainer]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteTrainer = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce formateur ?')) {
      const trainers = sectionData?.trainers || [];
      const updatedTrainers = trainers.filter((_, i) => i !== index);
      updateContent(`${contentPath}.trainers`, updatedTrainers);
      setSectionData(getContent(contentPath));
    }
  };

  const handleImagePositionSave = (trainerIndex, settings) => {
    updateContent(`${contentPath}.trainers.${trainerIndex}.photoSettings`, settings);
    setSectionData(getContent(contentPath));
    setEditingImageIndex(null);
  };

  const renderCvArrayEditor = (trainerIndex, field, label) => {
    const trainer = sectionData.trainers[trainerIndex];
    const items = trainer.cv?.[field] || [];

    return (
      <div className="features-section">
        <div className="features-header">
          <label>{label}</label>
          <button
            type="button"
            className="btn-add-feature"
            onClick={() => handleAddCvArrayItem(trainerIndex, field)}
          >
            <i className="fas fa-plus"></i>
            Ajouter
          </button>
        </div>
        <div className="features-list">
          {items.map((item, itemIndex) => (
            <div key={itemIndex} className="feature-item">
              <input
                type="text"
                value={item}
                onChange={(e) => handleCvArrayUpdate(trainerIndex, field, itemIndex, e.target.value)}
                placeholder={`${label}...`}
              />
              <button
                type="button"
                className="btn-delete-feature"
                onClick={() => handleDeleteCvArrayItem(trainerIndex, field, itemIndex)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!sectionData) {
    return (
      <div className="programs-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="programs-admin">
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-user-md"></i>
            Équipe de Formateurs Experts
          </h2>
          <p>Gérez votre équipe de formateurs qualifiés qui transforment votre pratique dentaire</p>
        </div>
        <button
          className="btn-add-program"
          onClick={handleAddTrainer}
        >
          <i className="fas fa-plus"></i>
          Ajouter un formateur
        </button>
      </div>

      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Nos Formateurs Experts"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Rencontrez notre équipe de formateurs qualifiés..."
            rows={2}
          />
        </div>
      </div>

      <div className="programs-list-admin">
        {(sectionData.trainers || []).map((trainer, index) => (
          <div key={index} className="program-item-admin">
            <div className="program-header">
              <div className="program-title-area">
                <h3>
                  <i className="fas fa-user-md"></i>
                  {trainer.name || `Formateur ${index + 1}`}
                </h3>
                {trainer.title && (
                  <span className="program-badge">
                    {trainer.title}
                  </span>
                )}
              </div>
              <div className="program-actions">
                <button
                  type="button"
                  className="btn-move"
                  onClick={() => handleMoveTrainer(index, 'up')}
                  disabled={index === 0}
                  title="Remonter"
                  aria-label="Remonter cet expert"
                >
                  <i className="fas fa-arrow-up" />
                </button>
                <button
                  type="button"
                  className="btn-move"
                  onClick={() => handleMoveTrainer(index, 'down')}
                  disabled={index === (sectionData.trainers || []).length - 1}
                  title="Descendre"
                  aria-label="Descendre cet expert"
                >
                  <i className="fas fa-arrow-down" />
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTrainer(index)}
                  title="Supprimer ce formateur"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Id</label>
                <input
                  type="text"
                  value={trainer.id || ''}
                  onChange={(e) => handleTrainerUpdate(index, 'id', e.target.value)}
                  placeholder="dr-ben-maamar"
                />
              </div>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={trainer.name || ''}
                  onChange={(e) => handleTrainerUpdate(index, 'name', e.target.value)}
                  placeholder="Dr. Ben Maamar"
                />
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={trainer.title || ''}
                  onChange={(e) => handleTrainerUpdate(index, 'title', e.target.value)}
                  placeholder="Formateur Senior en Implantologie"
                />
              </div>

              <div className="form-group">
                <label>Pays</label>
                <input
                  type="text"
                  value={trainer.country || ''}
                  onChange={(e) => handleTrainerUpdate(index, 'country', e.target.value)}
                  placeholder="Algérie"
                />
              </div>

              <div className="form-group">
                <label>Drapeau (emoji)</label>
                <input
                  type="text"
                  value={trainer.countryFlag || trainer.flagIcon || ''}
                  onChange={(e) => handleTrainerUpdate(index, 'countryFlag', e.target.value)}
                  placeholder="🇩🇿"
                />
              </div>

              <div className="form-group full-width">
                <label>Photo</label>
                <ImageUpload
                  value={trainer.photo}
                  onChange={(imageData) => handleTrainerUpdate(index, 'photo', imageData)}
                  label="Photo du formateur"
                />

                {trainer.photo && editingImageIndex !== index && (
                  <button
                    type="button"
                    onClick={() => setEditingImageIndex(index)}
                    style={{
                      marginTop: '10px',
                      padding: '10px 15px',
                      backgroundColor: '#00bcd4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-crop-alt" style={{ marginRight: '8px' }}></i>
                    Ajuster position et zoom
                  </button>
                )}

                {trainer.photo && editingImageIndex === index && (
                  <div style={{
                    marginTop: '15px',
                    padding: '20px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    border: '2px solid #00bcd4'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '15px'
                    }}>
                      <h4 style={{ margin: 0, color: '#00bcd4' }}>
                        <i className="fas fa-crop-alt" style={{ marginRight: '8px' }}></i>
                        Ajuster la photo du formateur
                      </h4>
                      <button
                        type="button"
                        onClick={() => setEditingImageIndex(null)}
                        style={{
                          padding: '8px 15px',
                          backgroundColor: '#666',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                        Fermer
                      </button>
                    </div>
                    <ImagePositionEditor
                      imageUrl={trainer.photo}
                      currentSettings={trainer.photoSettings}
                      onSave={(settings) => handleImagePositionSave(index, settings)}
                    />
                  </div>
                )}
              </div>

              <div className="form-group full-width">
                <label>Curriculum Vitae</label>
                <div className="form-group" style={{ marginTop: '10px' }}>
                  <label>Expérience</label>
                  <input
                    type="text"
                    value={trainer.cv?.experience || ''}
                    onChange={(e) => handleCvUpdate(index, 'experience', e.target.value)}
                    placeholder="15+ années d'expérience"
                  />
                </div>
                <div className="form-group full-width" style={{ marginTop: '15px' }}>
                  {renderCvArrayEditor(index, 'certifications', 'Certifications')}
                </div>
                <div className="form-group full-width" style={{ marginTop: '15px' }}>
                  {renderCvArrayEditor(index, 'achievements', 'Réalisations')}
                </div>
              </div>

              <div className="form-group full-width">
                <div className="features-section">
                  <div className="features-header">
                    <label>Spécialités</label>
                    <button
                      type="button"
                      className="btn-add-feature"
                      onClick={() => handleAddSpecialty(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>

                  <div className="features-list">
                    {(trainer.specialties || []).map((specialty, specialtyIndex) => (
                      <div key={specialtyIndex} className="feature-item">
                        <input
                          type="text"
                          value={specialty}
                          onChange={(e) => handleSpecialtyUpdate(index, specialtyIndex, e.target.value)}
                          placeholder="Spécialité..."
                        />
                        <button
                          type="button"
                          className="btn-delete-feature"
                          onClick={() => handleDeleteSpecialty(index, specialtyIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <div className="features-section">
                  <div className="features-header">
                    <label>Formations Animées</label>
                    <button
                      type="button"
                      className="btn-add-feature"
                      onClick={() => handleAddFormation(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>

                  <div className="features-list">
                    {(trainer.formations || []).map((formation, formationIndex) => (
                      <div key={formationIndex} className="feature-item">
                        <input
                          type="text"
                          value={formation}
                          onChange={(e) => handleFormationUpdate(index, formationIndex, e.target.value)}
                          placeholder="Formation..."
                        />
                        <button
                          type="button"
                          className="btn-delete-feature"
                          onClick={() => handleDeleteFormation(index, formationIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <div className="features-section">
                  <div className="features-header">
                    <label>Vidéos de Présentation (YouTube)</label>
                  </div>

                  {(trainer.presentationVideos || []).length > 0 && (
                    <div className="features-list" style={{ marginBottom: '15px' }}>
                      {(trainer.presentationVideos || []).map((video, videoIndex) => (
                        <div key={videoIndex} className="feature-item" style={{ alignItems: 'center' }}>
                          {video.thumbnail && (
                            <img
                              src={video.thumbnail}
                              alt=""
                              style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <input
                            type="text"
                            value={video.youtube_url || ''}
                            readOnly
                            placeholder="URL YouTube"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            className="btn-delete-feature"
                            onClick={() => handleDeletePresentationVideo(index, videoIndex)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <YouTubeVideoInput
                    onVideoAdd={(videoData) => handleAddPresentationVideo(index, videoData)}
                    label="Ajouter une vidéo de présentation"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(!sectionData.trainers || sectionData.trainers.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-user-md"></i>
          </div>
          <h3>Aucun formateur expert</h3>
          <p>Commencez par ajouter votre premier formateur expert.</p>
          <button className="btn-add-first" onClick={handleAddTrainer}>
            <i className="fas fa-plus"></i>
            Ajouter le premier formateur
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpertsTeamAdmin;
